<template>
  <v-dialog v-model="dialog" width="600px">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between text-subtitle-1 py-2">
        <span :style="[periodData?.periodProperties?.id.match(/PBG/gmi)?{'color':'blue'}:{'color':'red'}]">
          {{ periodData?.periodProperties?.description || 'Période' }}
        </span>
        <v-btn icon="mdi-close" density="compact" variant="text" @click="dialog = false" />
      </v-card-title>

      <v-card-subtitle class="text-caption py-1">
        <v-chip size="x-small" color="info" variant="tonal" class="mr-1">
          Période par défaut
        </v-chip>
        {{ formatDate(periodData?.periodProperties?.dateRange?.startDate) }}
        &rarr;
        {{ formatDate(periodData?.periodProperties?.dateRange?.endDate) }}
      </v-card-subtitle>

      <v-divider class="my-1" />

      <v-card-text class="pa-2">
        <!-- Message si aucune exception -->
        <div v-if="tableData.length === 0" class="text-center pa-4">
          <v-icon icon="mdi-check-circle" color="success" size="40" />
          <p class="text-subtitle-1 mt-2">Tous les magasins utilisent la période par défaut</p>
          <p class="text-caption text-grey">Aucune exception à afficher</p>
        </div>

        <!-- Tableau avec regroupement en arborescence -->
        <v-data-table
          v-else
          :headers="headers"
          :items="tableData"
          :items-per-page="100"
          :expanded="expanded"
          item-value="id"
          class="elevation-1 tree-table"
          hide-default-footer
          density="compact"
        >
          <!-- Colonne Groupe/Magasin -->
          <template #[`item.group`]="{ item }">
            <!-- Ligne de groupe (rayon) -->
            <div v-if="item.type === 'group'" class="d-flex align-center py-1">
              <v-icon 
                :icon="isGroupExpanded(item.id) ? 'mdi-chevron-down' : 'mdi-chevron-right'" 
                class="mr-2"
                @click="toggleGroup(item.id)"
                style="cursor: pointer;"
                size="small"
              />
              <div>
                <div class="font-weight-medium text-caption">
                  {{ item.groupName }}
                  <v-chip v-if="item.exceptionCount > 0" size="x-small" color="warning" class="ml-1">
                    {{ item.storeCount }} magasins 
                  </v-chip>
                </div>
               
              </div>
            </div>
            
            <!-- Ligne de magasin (famille) -->
            <div v-else class="ml-14 d-flex align-center">
              <v-avatar color="grey-lighten-4" size="24" class="mr-3">
                <v-icon size="small">mdi-store</v-icon>
              </v-avatar>
              <span class="font-weight-medium text-caption">{{ item.storeId }}</span>
              
            </div>
          </template>

          <!-- Colonne Date de début -->
          <template #[`item.startDate`]="{ item }">
            <span :class="item.type === 'group' ? 'text-grey' : ''" class="text-caption">
              {{ formatDate(item.startDate) }}
            </span>
          </template>

          <!-- Colonne Date de fin -->
          <template #[`item.endDate`]="{ item }">
            <span :class="item.type === 'group' ? 'text-grey' : ''" class="text-caption">
              {{ formatDate(item.endDate) }}
            </span>
          </template>

        
        </v-data-table>

        <!-- Footer avec statistiques -->
        <div v-if="tableData.length > 0" class="d-flex align-center justify-space-between pa-1 text-caption text-grey mt-2">
          <span>Total : {{ totalStores }} magasins </span>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="py-1">
        <v-spacer />
        <v-btn variant="text"  @click="dialog = false" color="red">Fermer</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog de détails d'exception -->
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  periodData: {
    type: Object,
    required: true,
    default: () => ({
      periodProperties: {
        description: 'Période',
        id: 'PBG-001',
        dateRange: {
          startDate: null,
          endDate: null
        }
      },
      storeExceptions: []
    })
  }
})

const dialog = defineModel()
const expanded = ref([])

// Définition des groupes
const STORE_GROUPS = {
  'Edisac France': ['MN01', 'LI02', 'DK03', 'FT04', 'RO06', 'AR09', 'EN11', 'CO18', 'HB21', 'LO07'],
  'Edisac Belgique': ['BR08', 'OO10', 'NP19', 'GE22'],
  'Etrier': ['VX05', 'CA24', 'PR25'],
  'Sites web': ['WEDI', 'WENL', 'WEBE', 'WETR', 'WOOM', 'WCAM']
}

const headers = [
  { title: 'Filiale / Magasin', key: 'group', sortable: false, width: '250px' },
  { title: 'Début', key: 'startDate', sortable: true, width: '110px' },
  { title: 'Fin', key: 'endDate', sortable: true, width: '110px' },
  { title: '', key: 'actions', sortable: false, width: '50px' }
]

// Fonction utilitaire pour comparer les dates
function datesAreEqual(date1, date2) {
  if (!date1 && !date2) return true
  if (!date1 || !date2) return false
  return date1 === date2
}

// Fonction pour vérifier si une date est différente de la période par défaut
function isDateDifferent(date, defaultDate) {
  return !datesAreEqual(date, defaultDate)
}

// Transforme storeExceptions en lignes exploitables
const storeRows = computed(() => {
  if (!props.periodData || !props.periodData.periodProperties) {
    return []
  }
  
  const exceptions = props.periodData.storeExceptions || []
  const defaultStart = props.periodData.periodProperties.dateRange?.startDate || null
  const defaultEnd = props.periodData.periodProperties.dateRange?.endDate || null

  const allStores = []
  
  // Ajouter tous les magasins des groupes définis
  for (const [groupName, stores] of Object.entries(STORE_GROUPS)) {
    for (const storeId of stores) {
      const exception = exceptions.find(e => e.storeId === storeId)
      
      // Récupérer les dates du magasin
      const storeStart = exception?.dateRange?.startDate || defaultStart
      const storeEnd = exception?.dateRange?.endDate || defaultEnd
      
      // Vérifier si les dates sont différentes de la période par défaut
      const hasDifferentDates = isDateDifferent(storeStart, defaultStart) || 
                               isDateDifferent(storeEnd, defaultEnd)
      
      // N'ajouter que si les dates sont différentes
      if (hasDifferentDates) {
        allStores.push({
          storeId,
          groupName,
          startDate: storeStart,
          endDate: storeEnd,
          isException: !!exception,
          exceptionData: exception || null,
          isDifferent: true
        })
      }
    }
  }

  // Ajouter les magasins qui sont dans les exceptions mais pas dans les groupes
  for (const exception of exceptions) {
    const existingStore = allStores.find(s => s.storeId === exception.storeId)
    if (!existingStore) {
      const storeStart = exception?.dateRange?.startDate || defaultStart
      const storeEnd = exception?.dateRange?.endDate || defaultEnd
      const hasDifferentDates = isDateDifferent(storeStart, defaultStart) || 
                               isDateDifferent(storeEnd, defaultEnd)
      
      if (hasDifferentDates) {
        allStores.push({
          storeId: exception.storeId,
          groupName: 'Autre',
          startDate: storeStart,
          endDate: storeEnd,
          isException: true,
          exceptionData: exception,
          isDifferent: true
        })
      }
    }
  }

  // Trier par groupe puis par storeId
  return allStores.sort((a, b) => {
    if (a.groupName !== b.groupName) return a.groupName.localeCompare(b.groupName)
    return a.storeId //.localeCompare(b.storeId)
  })
})

// Données pour le tableau en arborescence
const tableData = computed(() => {
  const result = []
  const groups = {}
  
  // Grouper les magasins par groupe
  storeRows.value.forEach(item => {
    if (!groups[item.groupName]) {
      groups[item.groupName] = []
    }
    groups[item.groupName].push(item)
  })

  // Construire les données du tableau
  let idCounter = 0
  
  for (const [groupName, stores] of Object.entries(groups)) {
    const groupId = `group-${idCounter++}`
    
    // Trier les magasins par storeId
    const sortedStores = [...stores] //.sort((a, b) => a.storeId.localeCompare(b.storeId))
    const firstStore = sortedStores[0]
    
    // Ajouter la ligne de groupe avec les dates du premier magasin
    result.push({
      id: groupId,
      type: 'group',
      groupName: groupName,
      storeCount: stores.length,
      exceptionCount: stores.filter(s => s.isException).length,
      startDate: firstStore?.startDate || null,
      endDate: firstStore?.endDate || null,
      isDifferent: true
    })
    
    // Ajouter les magasins du groupe (si le groupe est expansé)
    if (isGroupExpanded(groupId)) {
      sortedStores.forEach(store => {
        result.push({
          id: `store-${idCounter++}`,
          type: 'store',
          groupId: groupId,
          ...store
        })
      })
    }
  }
  
  return result
})

// Vérifier si un groupe est expansé
function isGroupExpanded(groupId) {
  return expanded.value.includes(groupId)
}

// Toggle d'un groupe
function toggleGroup(groupId) {
  const index = expanded.value.indexOf(groupId)
  if (index > -1) {
    expanded.value.splice(index, 1)
  } else {
    expanded.value.push(groupId)
  }
  // Forcer la réactivité
  expanded.value = [...expanded.value]
}



// Formater une date
function formatDate(isoDate) {
  if (!isoDate) return '-'
  try {
    return new Date(isoDate).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch {
    return '-'
  }
}

// Computed pour les statistiques
const totalStores = computed(() => storeRows.value.length)
</script>

<style scoped>
.tree-table :deep(.v-data-table__td) {
  padding: 2px 8px !important;
  font-size: 0.75rem !important;
}

.tree-table :deep(.v-data-table__th) {
  padding: 2px 8px !important;
  font-size: 0.65rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f5f5f5 !important;
}

.tree-table :deep(.v-data-table tr) {
  min-height: 32px !important;
}

/* Style pour les lignes de groupe */
.tree-table :deep(.v-data-table tbody tr:has(.v-icon)) {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #e9ecef;
}

.tree-table :deep(.v-data-table tbody tr:has(.v-icon):hover) {
  background-color: #f1f3f5 !important;
}

/* Style pour les lignes de magasins */
.tree-table :deep(.v-data-table tbody tr:not(:has(.v-icon))) {
  background-color: #ffffff !important;
}

.tree-table :deep(.v-data-table tbody tr:not(:has(.v-icon)):nth-child(even)) {
  background-color: #fafafa !important;
}

/* Style pour les lignes avec exceptions */
.tree-table :deep(.v-data-table tbody tr:has(.v-chip-warning)) {
  background-color: #fff8e1 !important;
}

.tree-table :deep(.v-data-table tbody tr:has(.v-chip-warning):hover) {
  background-color: #ffecb3 !important;
}

/* Réduire la taille des chips */
:deep(.v-chip) {
  font-size: 0.6rem !important;
  padding: 0 6px !important;
  height: 18px !important;
}

/* Icônes plus petites */
:deep(.v-icon) {
  font-size: 18px !important;
}

/* Style pour le message "aucune exception" */
.text-center {
  text-align: center;
}
</style>